// Lightweight in-memory ring buffers for runtime diagnostics.
// Captures recent console errors, unhandled rejections, and failed fetches
// so the bug-report dialog can ship them with the user's submission.

export interface ErrorEntry {
  ts: number;
  type: "error" | "unhandledrejection" | "console.error";
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
}

export interface NetworkEntry {
  ts: number;
  url: string;
  method: string;
  status: number;
  durationMs: number;
}

const MAX_ERRORS = 20;
const MAX_NET = 10;

const errorBuf: ErrorEntry[] = [];
const netBuf: NetworkEntry[] = [];
let installed = false;

function push<T>(arr: T[], entry: T, cap: number) {
  arr.push(entry);
  if (arr.length > cap) arr.splice(0, arr.length - cap);
}

function redactUrl(u: string): string {
  try {
    const url = new URL(u, window.location.origin);
    // Strip query strings entirely (may contain tokens) and any hash.
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return u.split("?")[0];
  }
}

function safeMessage(v: unknown): string {
  if (v == null) return String(v);
  if (typeof v === "string") return v.slice(0, 1000);
  if (v instanceof Error) return (v.message || String(v)).slice(0, 1000);
  try {
    return JSON.stringify(v).slice(0, 1000);
  } catch {
    return String(v).slice(0, 1000);
  }
}

export function installDiagnosticsBuffer() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e) => {
    push(
      errorBuf,
      {
        ts: Date.now(),
        type: "error",
        message: safeMessage(e.message || e.error),
        stack: e.error?.stack?.slice(0, 2000),
        source: e.filename ? redactUrl(e.filename) : undefined,
        line: e.lineno,
        col: e.colno,
      },
      MAX_ERRORS,
    );
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    push(
      errorBuf,
      {
        ts: Date.now(),
        type: "unhandledrejection",
        message: safeMessage(reason),
        stack: reason?.stack?.slice(0, 2000),
      },
      MAX_ERRORS,
    );
  });

  // Wrap console.error
  const origErr = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      push(
        errorBuf,
        {
          ts: Date.now(),
          type: "console.error",
          message: args.map(safeMessage).join(" ").slice(0, 1500),
        },
        MAX_ERRORS,
      );
    } catch {
      /* noop */
    }
    origErr(...args);
  };

  // Wrap fetch to capture failures (status >= 400 or thrown)
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const start = performance.now();
    const method = (init?.method || (typeof input !== "string" && "method" in (input as Request) ? (input as Request).method : "GET")).toUpperCase();
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : (input as Request).url;
    try {
      const res = await origFetch(input as RequestInfo, init);
      if (res.status >= 400) {
        push(
          netBuf,
          {
            ts: Date.now(),
            url: redactUrl(url),
            method,
            status: res.status,
            durationMs: Math.round(performance.now() - start),
          },
          MAX_NET,
        );
      }
      return res;
    } catch (err) {
      push(
        netBuf,
        {
          ts: Date.now(),
          url: redactUrl(url),
          method,
          status: 0,
          durationMs: Math.round(performance.now() - start),
        },
        MAX_NET,
      );
      throw err;
    }
  };
}

export function getRecentErrors(): ErrorEntry[] {
  return errorBuf.slice();
}

export function getRecentNetworkFailures(): NetworkEntry[] {
  return netBuf.slice();
}

export { redactUrl };
